import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Row, Col, Statistic, Tag, message, Spin, Drawer, Divider, Space, Tooltip, Empty, Modal, Form, Input, Select, InputNumber, Switch, Tabs, Alert, Progress } from 'antd';
import { UserOutlined, CheckOutlined, HeartOutlined, SafetyOutlined, DollarOutlined, EyeOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer } from 'recharts';
import apiService from "services/api/apiService";

interface Benefit {
  id: number;
  benefit_name: string;
  benefit_type: string;
  description: string;
  employer_contribution: number;
  employee_contribution: number;
  is_active: number;
  effective_date?: string;
  expiry_date?: string;
}

interface BenefitsSummary {
  total_enrolled_employees: number;
  total_benefit_types: number;
  active_enrollments: number;
  inactive_enrollments: number;
  total_employee_contribution: number;
  total_employer_contribution: number;
  total_cost: number;
}

interface BenefitsAnalytic {
  benefit_name: string;
  benefit_type: string;
  enrollment_count: number;
  total_employee_cost: number;
  total_employer_cost: number;
}

const BenefitsManagement: React.FC = () => {
  const [benefits, setBenefits] = useState<Benefit[]>([]);
  const [summary, setSummary] = useState<BenefitsSummary | null>(null);
  const [analytics, setAnalytics] = useState<BenefitsAnalytic[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('benefits');
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [costReport, setCostReport] = useState<any>(null);
  const [hmoProviders, setHmoProviders] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [enrollModal, setEnrollModal] = useState(false);
  const [selectedBenefitForEnrol, setSelectedBenefitForEnrol] = useState<any>(null);
  const [enrollForm] = Form.useForm();
  const [detailDrawer, setDetailDrawer] = useState(false);
  const [selectedBenefit, setSelectedBenefit] = useState<Benefit | null>(null);
  
  // CRUD Modal States
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<Benefit | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [benefitsRes, summaryRes, analyticsRes] = await Promise.all([
        apiService.listBenefits(),
        apiService.getBenefitsSummary(),
        apiService.getBenefitsAnalytics()
      ]);

      if (benefitsRes.success) setBenefits(benefitsRes.data || []);
      if (summaryRes.success) setSummary(summaryRes.data);
      if (analyticsRes.success) setAnalytics(analyticsRes.data || []);
    } catch (error) {
      message.error('Failed to fetch benefits');
    } finally {
      setLoading(false);
    }
  };

  // CRUD Handlers
  const handleCreate = () => {
    form.resetFields();
    setCreateModalVisible(true);
  };

  const handleEdit = (record: Benefit) => {
    setSelectedRecord(record);
    form.setFieldsValue({
      benefit_name: record.benefit_name,
      benefit_type: record.benefit_type,
      description: record.description,
      employer_contribution: record.employer_contribution,
      employee_contribution: record.employee_contribution,
      is_active: Boolean(record.is_active),
    });
    setEditModalVisible(true);
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: 'Delete Benefit',
      content: 'Are you sure you want to delete this benefit? This will also remove all employee enrollments.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      async onOk() {
        try {
          const response = await apiService.deleteBenefit(id);
          if (response.success) {
            message.success('Benefit deleted successfully');
            fetchData();
          } else {
            message.error(response.error || 'Failed to delete benefit');
          }
        } catch (error) {
          console.error('Delete error:', error);
          message.error('Failed to delete benefit');
        }
      },
    });
  };

  const handleCreateSubmit = async (values: any) => {
    try {
      const response = await apiService.createBenefit({
        ...values,
        is_active: values.is_active ? 1 : 0,
        effective_date: new Date().toISOString().split('T')[0]
      });
      
      if (response.success) {
        message.success('Benefit created successfully');
        setCreateModalVisible(false);
        form.resetFields();
        fetchData();
      } else {
        message.error(response.error || 'Failed to create benefit');
      }
    } catch (error) {
      console.error('Create error:', error);
      message.error('Failed to create benefit');
    }
  };

  const handleEditSubmit = async (values: any) => {
    if (!selectedRecord) return;
    
    try {
      const response = await apiService.updateBenefit(selectedRecord.id, {
        ...values,
        is_active: values.is_active ? 1 : 0
      });
      
      if (response.success) {
        message.success('Benefit updated successfully');
        setEditModalVisible(false);
        setSelectedRecord(null);
        form.resetFields();
        fetchData();
      } else {
        message.error(response.error || 'Failed to update benefit');
      }
    } catch (error) {
      console.error('Update error:', error);
      message.error('Failed to update benefit');
    }
  };

  const getBenefitIcon = (type: string) => {
    switch (type) {
      case 'health_insurance': return <HeartOutlined style={{ color: '#f5222d' }} />;
      case 'life_insurance': return <SafetyOutlined style={{ color: '#1890ff' }} />;
      case 'retirement_plan': return <DollarOutlined style={{ color: '#52c41a' }} />;
      default: return <CheckOutlined />;
    }
  };

  const benefitTypes = [
    { label: 'Health Insurance', value: 'health_insurance' },
    { label: 'Life Insurance', value: 'life_insurance' },
    { label: 'Retirement Plan', value: 'retirement_plan' },
    { label: 'PTO Accrual', value: 'pto_accrual' },
    { label: 'Bonus Scheme', value: 'bonus_scheme' },
    { label: 'Allowance', value: 'allowance' },
    { label: 'Other', value: 'other' }
  ];

  const benefitColumns = [
    {
      title: 'Benefit Name',
      key: 'benefit_name',
      render: (_: any, record: Benefit) => (
        <Space>
          {getBenefitIcon(record.benefit_type)}
          <span>{record.benefit_name}</span>
        </Space>
      ),
      width: 200
    },
    {
      title: 'Type',
      dataIndex: 'benefit_type',
      render: (type: string) => <Tag color="blue">{type.replace(/_/g, ' ')}</Tag>,
      width: 150
    },
    {
      title: 'Employer Contribution',
      dataIndex: 'employer_contribution',
      render: (v: string | number) => `₦${parseFloat(String(v || 0)).toFixed(2)}`,
      align: 'right' as const,
      width: 150
    },
    {
      title: 'Employee Contribution',
      dataIndex: 'employee_contribution',
      render: (v: string | number) => `₦${parseFloat(String(v || 0)).toFixed(2)}`,
      align: 'right' as const,
      width: 150
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      render: (active: number) => <Tag color={active ? 'green' : 'red'}>{active ? 'Active' : 'Inactive'}</Tag>,
      width: 100
    },
    {
      title: 'Actions',
      render: (_: any, record: Benefit) => (
        <Space>
          <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => { setSelectedBenefit(record); setDetailDrawer(true); }} />
          <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Button type="text" size="small" icon={<DeleteOutlined />} danger onClick={() => handleDelete(record.id)} />
        </Space>
      ),
      width: 120
    }
  ];

  const COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1'];

  const fetchExtras = async () => {
    try {
      const [er, cr, hr, emr] = await Promise.all([
        apiService.getBenefitEnrollments(),
        apiService.getBenefitCostReport(),
        apiService.getHMOProviders(),
        apiService.getEmployees(),
      ]);
      if (er.success) setEnrollments(er.data || []);
      if (cr.success) setCostReport(cr.data);
      if (hr.success) setHmoProviders(hr.data || []);
      if (emr.success) setEmployees(emr.data || []);
    } catch {}
  };

  React.useEffect(() => {
    if (activeTab !== 'benefits') fetchExtras();
  }, [activeTab]);

  const handleEnrol = async (values: any) => {
    const r = await apiService.enrollEmployeeInBenefit({
      ...values,
      benefit_id: selectedBenefitForEnrol?.id,
      enrollment_date: new Date().toISOString().split('T')[0],
    });
    if (r.success) {
      message.success('Employee enrolled successfully');
      setEnrollModal(false);
      enrollForm.resetFields();
      fetchExtras();
    } else message.error(r.error || 'Failed');
  };

  const fmt = (v: any) => '₦' + parseFloat(String(v || 0)).toLocaleString('en-NG', { minimumFractionDigits: 2 });

  return (
    <div style={{ padding: 24 }}>
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
        {
          key: 'benefits',
          label: '🏥 Benefits Catalogue',
          children: (
            <div>
                    <h1 style={{ marginBottom: '24px' }}>Benefits Management</h1>
              
                    <Row gutter={16} style={{ marginBottom: '24px' }}>
                      <Col xs={24} sm={12} lg={6}>
                        <Card loading={!summary}>
                          <Statistic title="Enrolled Employees" value={summary?.total_enrolled_employees || 0} />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <Card loading={!summary}>
                          <Statistic title="Active Enrollments" value={summary?.active_enrollments || 0} />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <Card loading={!summary}>
                          <Statistic title="Total Cost" value={((summary?.total_employee_contribution || 0) + (summary?.total_employer_contribution || 0)) / 12} prefix="₦" precision={2} suffix="/mo" />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <Card loading={!summary}>
                          <Statistic title="Benefit Types" value={summary?.total_benefit_types || 0} />
                        </Card>
                      </Col>
                    </Row>
              
                    <Row gutter={16} style={{ marginBottom: '24px' }}>
                      <Col xs={24} lg={12}>
                        <Card title="Benefit Enrollment by Type">
                          {analytics.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                              <BarChart data={analytics}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="benefit_name" angle={-45} textAnchor="end" height={80} />
                                <YAxis />
                                <ChartTooltip />
                                <Bar dataKey="enrollment_count" fill="#1890ff" />
                              </BarChart>
                            </ResponsiveContainer>
                          ) : <Empty />}
                        </Card>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Card title="Cost Distribution">
                          {analytics.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                              <PieChart>
                                <Pie
                                  data={analytics.map(item => ({ name: item.benefit_name, value: item.enrollment_count }))}
                                  cx="50%" cy="50%" outerRadius={100} fill="#8884d8" dataKey="value"
                                >
                                  {COLORS.map((c, i) => <Cell key={i} fill={c} />)}
                                </Pie>
                                <ChartTooltip />
                              </PieChart>
                            </ResponsiveContainer>
                          ) : <Empty />}
                        </Card>
                      </Col>
                    </Row>
              
                    <Card 
                      title="Available Benefits" 
                      loading={loading} 
                      extra={
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                          Add Benefit
                        </Button>
                      }
                    >
                      <Table columns={benefitColumns} dataSource={benefits} rowKey="id" pagination={{ pageSize: 10 }} scroll={{ x: 1000 }} />
                    </Card>
              
                    {/* View Details Drawer */}
                    <Drawer title="Benefit Details" placement="right" onClose={() => setDetailDrawer(false)} open={detailDrawer} width={500}>
                      {selectedBenefit && (
                        <div>
                          <Divider>Benefit Information</Divider>
                          <p><strong>Benefit Name:</strong> {selectedBenefit.benefit_name}</p>
                          <p><strong>Type:</strong> {selectedBenefit.benefit_type}</p>
                          <p><strong>Description:</strong> {selectedBenefit.description}</p>
                          <Divider>Contributions</Divider>
                          <Row gutter={16}>
                            <Col span={12}><Statistic title="Employer" value={selectedBenefit.employer_contribution} prefix="₦" /></Col>
                            <Col span={12}><Statistic title="Employee" value={selectedBenefit.employee_contribution} prefix="₦" /></Col>
                          </Row>
                          <Divider>Status</Divider>
                          <Tag color={selectedBenefit.is_active ? 'green' : 'red'}>{selectedBenefit.is_active ? 'ACTIVE' : 'INACTIVE'}</Tag>
                        </div>
                      )}
                    </Drawer>
              
                    {/* Create Benefit Modal */}
                    <Modal
                      title="Create New Benefit"
                      open={createModalVisible}
                      onCancel={() => {
                        setCreateModalVisible(false);
                        form.resetFields();
                      }}
                      footer={null}
                      width={600}
                    >
                      <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleCreateSubmit}
                      >
                        <Form.Item
                          name="benefit_name"
                          label="Benefit Name"
                          rules={[{ required: true, message: 'Please enter benefit name' }]}
                        >
                          <Input placeholder="e.g., Health Insurance Premium" />
                        </Form.Item>
              
                        <Form.Item
                          name="benefit_type"
                          label="Benefit Type"
                          rules={[{ required: true, message: 'Please select benefit type' }]}
                        >
                          <Select placeholder="Select benefit type" options={benefitTypes} />
                        </Form.Item>
              
                        <Form.Item
                          name="description"
                          label="Description"
                        >
                          <Input.TextArea rows={3} placeholder="Brief description of the benefit" />
                        </Form.Item>
              
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item
                              name="employer_contribution"
                              label="Employer Contribution (₦)"
                              rules={[{ required: true, message: 'Please enter employer contribution' }]}
                            >
                              <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              name="employee_contribution"
                              label="Employee Contribution (₦)"
                              rules={[{ required: true, message: 'Please enter employee contribution' }]}
                            >
                              <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                        </Row>
              
                        <Form.Item
                          name="is_active"
                          label="Status"
                          valuePropName="checked"
                          initialValue={true}
                        >
                          <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                        </Form.Item>
              
                        <Form.Item>
                          <Space>
                            <Button type="primary" htmlType="submit">
                              Create Benefit
                            </Button>
                            <Button onClick={() => {
                              setCreateModalVisible(false);
                              form.resetFields();
                            }}>
                              Cancel
                            </Button>
                          </Space>
                        </Form.Item>
                      </Form>
                    </Modal>
              
                    {/* Edit Benefit Modal */}
                    <Modal
                      title="Edit Benefit"
                      open={editModalVisible}
                      onCancel={() => {
                        setEditModalVisible(false);
                        setSelectedRecord(null);
                        form.resetFields();
                      }}
                      footer={null}
                      width={600}
                    >
                      <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleEditSubmit}
                      >
                        <Form.Item
                          name="benefit_name"
                          label="Benefit Name"
                          rules={[{ required: true, message: 'Please enter benefit name' }]}
                        >
                          <Input placeholder="e.g., Health Insurance Premium" />
                        </Form.Item>
              
                        <Form.Item
                          name="benefit_type"
                          label="Benefit Type"
                          rules={[{ required: true, message: 'Please select benefit type' }]}
                        >
                          <Select placeholder="Select benefit type" options={benefitTypes} />
                        </Form.Item>
              
                        <Form.Item
                          name="description"
                          label="Description"
                        >
                          <Input.TextArea rows={3} placeholder="Brief description of the benefit" />
                        </Form.Item>
              
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item
                              name="employer_contribution"
                              label="Employer Contribution (₦)"
                              rules={[{ required: true, message: 'Please enter employer contribution' }]}
                            >
                              <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              name="employee_contribution"
                              label="Employee Contribution (₦)"
                              rules={[{ required: true, message: 'Please enter employee contribution' }]}
                            >
                              <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                        </Row>
              
                        <Form.Item
                          name="is_active"
                          label="Status"
                          valuePropName="checked"
                        >
                          <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                        </Form.Item>
              
                        <Form.Item>
                          <Space>
                            <Button type="primary" htmlType="submit">
                              Update Benefit
                            </Button>
                            <Button onClick={() => {
                              setEditModalVisible(false);
                              setSelectedRecord(null);
                              form.resetFields();
                            }}>
                              Cancel
                            </Button>
                          </Space>
                        </Form.Item>
                      </Form>
                    </Modal>
            </div>
          ),
        },
        {
          key: 'enrolments',
          label: '📋 Enrolments',
          children: (
            <Card title="Employee Benefit Enrolments" extra={<Button type="primary" onClick={fetchExtras}>Refresh</Button>}>
              <Table dataSource={enrollments} rowKey="id" pagination={{ pageSize: 15 }}
                columns={[
                  { title: 'Employee', dataIndex: 'employee_name', render: (v: string, r: any) => <><div style={{ fontWeight: 600 }}>{v}</div><div style={{ fontSize: 11, color: '#999' }}>{r.department}</div></> },
                  { title: 'Benefit', dataIndex: 'benefit_name' },
                  { title: 'Type', dataIndex: 'benefit_type', render: (v: string) => <Tag color="blue">{v?.replace('_', ' ')}</Tag> },
                  { title: 'Enrolled', dataIndex: 'enrollment_date' },
                  { title: 'Employee Cost', dataIndex: 'employee_contribution', render: fmt },
                  { title: 'Employer Cost', dataIndex: 'employer_contribution', render: fmt },
                  { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag color={v === 'active' ? 'success' : 'warning'}>{v?.toUpperCase()}</Tag> },
                  { title: 'Actions', render: (_: any, r: any) => r.status === 'active' ? (
                    <Button size="small" danger onClick={async () => {
                      const res = await apiService.unenrolEmployeeFromBenefit(r.employee_id, r.benefit_id);
                      if (res.success) { message.success('Unenrolled'); fetchExtras(); }
                      else message.error(res.error || 'Failed');
                    }}>Unenrol</Button>
                  ) : null },
                ]} />
            </Card>
          ),
        },
        {
          key: 'hmo',
          label: '🏨 HMO Providers',
          children: (
            <div>
              <Alert message="Connect with Nigerian HMO providers for health insurance." type="info" showIcon style={{ marginBottom: 16 }} />
              <Row gutter={16}>
                {hmoProviders.map((p: any, i: number) => (
                  <Col span={8} key={i} style={{ marginBottom: 16 }}>
                    <Card size="small" title={p.name} extra={<Tag color="blue">HMO</Tag>}>
                      <p style={{ fontSize: 12, color: '#666' }}>Coverage: {p.coverage_types?.join(', ')}</p>
                      <p style={{ fontSize: 12 }}>Website: <a href={`https://${p.website}`} target="_blank" rel="noreferrer">{p.website}</a></p>
                      <Button size="small" type="primary" style={{ marginTop: 8 }} onClick={() => message.info(`Contact ${p.name} to set up a corporate HMO plan`)}>Get Quote</Button>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          ),
        },
        {
          key: 'cost',
          label: '💰 Cost Tracking',
          children: (
            <div>
              {costReport?.totals && (
                <Row gutter={16} style={{ marginBottom: 24 }}>
                  <Col span={6}><Card><Statistic title="Enrolled" value={costReport.totals.enrolled_count || 0} /></Card></Col>
                  <Col span={6}><Card><Statistic title="Employer/Month" value={costReport.totals.monthly_employer_cost || 0} prefix="₦" formatter={(v: any) => Number(v).toLocaleString()} /></Card></Col>
                  <Col span={6}><Card><Statistic title="Employee/Month" value={costReport.totals.monthly_employee_cost || 0} prefix="₦" formatter={(v: any) => Number(v).toLocaleString()} /></Card></Col>
                  <Col span={6}><Card><Statistic title="Total/Month" value={costReport.totals.total_monthly_cost || 0} prefix="₦" formatter={(v: any) => Number(v).toLocaleString()} valueStyle={{ color: '#1890ff' }} /></Card></Col>
                </Row>
              )}
              <Card title="Cost by Department">
                <Table dataSource={costReport?.by_department || []} rowKey="department" pagination={false}
                  columns={[
                    { title: 'Department', dataIndex: 'department' },
                    { title: 'Enrolled', dataIndex: 'enrolled_employees' },
                    { title: 'Employee Cost', dataIndex: 'total_employee_cost', render: fmt },
                    { title: 'Employer Cost', dataIndex: 'total_employer_cost', render: fmt },
                    { title: 'Total Cost', dataIndex: 'total_cost', render: (v: any) => <strong style={{ color: '#1890ff' }}>{fmt(v)}</strong> },
                  ]} />
              </Card>
            </div>
          ),
        },
      ]} />

      {/* Enrolment Modal */}
      <Modal title={`Enrol Employee — ${selectedBenefitForEnrol?.benefit_name || ''}`}
        open={enrollModal} onCancel={() => { setEnrollModal(false); enrollForm.resetFields(); }} footer={null} width={500}>
        <Form form={enrollForm} layout="vertical" onFinish={handleEnrol}>
          <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children">
              {employees.map((e: any) => <Select.Option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.department}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="coverage_amount" label="Coverage Amount (₦)"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="notes" label="Notes"><Input.TextArea rows={2} /></Form.Item>
          <Space><Button type="primary" htmlType="submit">Enrol</Button><Button onClick={() => setEnrollModal(false)}>Cancel</Button></Space>
        </Form>
      </Modal>
    </div>
  );
};

export default BenefitsManagement;
